/**
 * ResetPasswordPage — consume a ?token=… link and set a new password.
 *
 * Mirrors the LoginPage signup form's strength meter so the UX is identical.
 * On success, shows a friendly confirmation and routes back to /login.
 */
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import ConnectionSourceLogo from "../components/ConnectionSourceLogo";

const API = process.env.REACT_APP_BACKEND_URL;

function computeStrength(pw) {
  const checks = [
    { id: "length", ok: pw.length >= 10, label: "10+ characters" },
    { id: "lower",  ok: /[a-z]/.test(pw), label: "lowercase" },
    { id: "upper",  ok: /[A-Z]/.test(pw), label: "uppercase" },
    { id: "number", ok: /\d/.test(pw),    label: "number" },
    { id: "symbol", ok: /[^A-Za-z0-9]/.test(pw), label: "symbol" },
  ];
  return { score: checks.filter((c) => c.ok).length, checks };
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const strength = useMemo(() => computeStrength(password), [password]);
  const match = password.length > 0 && password === confirm;
  const canSubmit = !!token && strength.score >= 4 && match && !loading;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, password });
      setDone(true);
      toast.success("Password updated");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d.msg).filter(Boolean).join(" · ")
          : "Couldn't reset your password — try requesting a new link.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cs-login-root min-h-screen relative flex items-center justify-center p-4 overflow-hidden">
      <div aria-hidden className="cs-login-aurora" />
      <div aria-hidden className="cs-login-orb" />
      <div aria-hidden className="cs-login-grid" />
      <div aria-hidden className="cs-login-scanner" />

      <div className="cs-login-card relative z-10 w-full max-w-md rounded-3xl overflow-hidden">
        <div
          aria-hidden
          className="absolute top-0 left-0 right-0 h-[2px]"
          style={{ background: "linear-gradient(90deg, transparent, #CD040B, #F59E0B, #CD040B, transparent)" }}
        />
        <div className="cs-login-inner relative p-7">
          <div className="flex justify-center mb-5">
            <ConnectionSourceLogo size="md" />
          </div>

          {!token ? (
            <InvalidLinkState onBack={() => navigate("/forgot-password")} />
          ) : done ? (
            <DoneState onContinue={() => navigate("/login")} />
          ) : (
            <>
              <div className="text-center mb-5">
                <h1
                  className="text-2xl font-black leading-tight mb-1 tracking-tight"
                  style={{ fontFamily: "Outfit, sans-serif", color: "var(--cs-text)" }}
                >
                  Choose a new password
                </h1>
                <p className="text-xs" style={{ color: "var(--cs-text-muted)" }}>
                  Pick something strong — you'll use it next time you sign in.
                </p>
              </div>

              <form onSubmit={onSubmit} className="space-y-3" data-testid="reset-password-form">
                <PasswordField
                  label="New password"
                  value={password}
                  onChange={setPassword}
                  show={show}
                  setShow={setShow}
                  testid="reset-password-input"
                />
                <StrengthMeter strength={strength} />
                <PasswordField
                  label="Confirm new password"
                  value={confirm}
                  onChange={setConfirm}
                  show={show}
                  setShow={setShow}
                  testid="reset-confirm-input"
                  state={confirm ? (match ? "ok" : "bad") : "neutral"}
                  hint={confirm && !match ? "Passwords don't match yet." : null}
                />
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="cs-submit-btn w-full py-3 rounded-xl text-sm font-black flex items-center justify-center gap-2 overflow-hidden relative"
                  data-testid="reset-submit-btn"
                >
                  <span className="relative z-10" style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "0.5px" }}>
                    {loading ? "Updating…" : "Update password"}
                  </span>
                  {loading && <span className="cs-spinner" aria-hidden />}
                </button>

                <div className="pt-1 text-center">
                  <button
                    type="button"
                    onClick={() => navigate("/login")}
                    className="cs-forgot-link text-[11px] font-semibold tracking-wide"
                    data-testid="reset-back-login-btn"
                  >
                    ← Back to sign in
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── Subviews ─────────────────────── */

function InvalidLinkState({ onBack }) {
  return (
    <div className="text-center py-2" data-testid="reset-invalid-state">
      <div
        className="mx-auto mb-4 w-14 h-14 rounded-full flex items-center justify-center"
        style={{
          background: "rgba(239,68,68,0.15)",
          border: "1px solid rgba(239,68,68,0.4)",
          boxShadow: "0 0 24px rgba(239,68,68,0.25)",
        }}
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      </div>
      <h1 className="text-2xl font-black mb-2 tracking-tight" style={{ fontFamily: "Outfit, sans-serif", color: "var(--cs-text)" }}>
        Link looks broken
      </h1>
      <p className="text-xs mb-5" style={{ color: "var(--cs-text-muted)" }}>
        This reset link is missing its token. Request a fresh one.
      </p>
      <button type="button" onClick={onBack} className="cs-submit-btn w-full py-3 rounded-xl text-sm font-black" data-testid="reset-invalid-retry-btn">
        <span style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "0.5px" }}>Request a new link</span>
      </button>
    </div>
  );
}

function DoneState({ onContinue }) {
  return (
    <div className="text-center py-2" data-testid="reset-done-state">
      <div
        className="mx-auto mb-4 w-14 h-14 rounded-full flex items-center justify-center"
        style={{
          background: "linear-gradient(135deg, rgba(16,185,129,0.18), rgba(6,182,212,0.18))",
          border: "1px solid rgba(16,185,129,0.4)",
          boxShadow: "0 0 32px rgba(16,185,129,0.3)",
        }}
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h1 className="text-2xl font-black mb-2 tracking-tight" style={{ fontFamily: "Outfit, sans-serif", color: "var(--cs-text)" }}>
        You're all set
      </h1>
      <p className="text-xs mb-5" style={{ color: "var(--cs-text-muted)" }}>
        Your password has been updated. Sign in with your new credentials.
      </p>
      <button type="button" onClick={onContinue} className="cs-submit-btn w-full py-3 rounded-xl text-sm font-black" data-testid="reset-done-continue-btn">
        <span style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "0.5px" }}>Continue to sign in</span>
      </button>
    </div>
  );
}

function PasswordField({ label, value, onChange, show, setShow, testid, state, hint }) {
  const ring = state === "ok" ? "#10B981" : state === "bad" ? "#EF4444" : "#CD040B";
  return (
    <div>
      <label className="cs-floating-field block relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="new-password"
          data-testid={testid}
          className="cs-floating-input peer w-full px-4 pt-5 pb-2 rounded-xl outline-none text-sm"
          style={{
            background: "var(--cs-bg-hover)",
            border: "1px solid var(--cs-border)",
            color: "var(--cs-text)",
            "--ring-color": ring,
          }}
        />
        <span className={`cs-floating-label absolute left-4 pointer-events-none transition-all ${value ? "is-up" : ""}`}>
          {label}
        </span>
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold cs-eye-btn"
        >
          {show ? "HIDE" : "SHOW"}
        </button>
      </label>
      {hint && (
        <div className="text-[10px] mt-1 ml-1 font-semibold" style={{ color: "#EF4444" }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function StrengthMeter({ strength }) {
  const { score, checks } = strength;
  const labels = ["Add a password", "Very weak", "Weak", "Okay", "Strong", "Rock-solid"];
  const colors = ["#52525B", "#EF4444", "#F97316", "#F59E0B", "#10B981", "#059669"];
  return (
    <div className="mt-2" data-testid="reset-strength">
      <div className="flex gap-1" aria-hidden>
        {[0, 1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className="flex-1 h-1 rounded-full transition-all duration-300"
            style={{
              background: i < score ? colors[score] : "var(--cs-border)",
              boxShadow: i < score ? `0 0 8px ${colors[score]}55` : "none",
            }}
          />
        ))}
      </div>
      <div className="mt-1.5 flex items-center justify-between">
        <span className="text-[10px] font-bold" style={{ color: colors[score] }}>{labels[score]}</span>
        <span className="text-[10px] tabular-nums" style={{ color: "var(--cs-text-muted)" }}>{score}/5</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {checks.map((c) => (
          <span
            key={c.id}
            className="text-[9px] font-bold px-2 py-0.5 rounded-full transition-all"
            style={{
              background: c.ok ? "rgba(16,185,129,0.14)" : "var(--cs-bg-hover)",
              color: c.ok ? "#10B981" : "var(--cs-text-muted)",
              border: c.ok ? "1px solid rgba(16,185,129,0.4)" : "1px solid var(--cs-border)",
            }}
          >
            {c.ok ? "✓" : "•"} {c.label}
          </span>
        ))}
      </div>
    </div>
  );
}
